import { MenuOutlined } from '@ant-design/icons';
import { useAuth0 } from '@auth0/auth0-react';
import { Button, Drawer, Grid } from 'antd';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { mcpAllowedScopes } from '@clh/mcp-scopes';
import { unwrapError, useConfig } from '@clh/ui';

import { useToast } from '../hooks/use-toast';

import Chat from './chat';
import { ChatSidebar } from './chat-sidebar';

const { useBreakpoint } = Grid;

export default function ChatLayout() {
    const config = useConfig();
    const { getAccessTokenSilently } = useAuth0();
    const { notification } = useToast();
    const [chatToken, setChatToken] = useState<string | null>(null);
    const { conversationId: urlConversationId } = useParams<{
        conversationId: string;
    }>();
    const navigate = useNavigate();
    const [activeConversationId, setActiveConversationId] = useState<
        string | null
    >(urlConversationId ?? null);
    const [chatKey, setChatKey] = useState(0);
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const breakpoints = useBreakpoint();
    const isMobile = !breakpoints.md;

    useEffect(() => {
        setActiveConversationId(urlConversationId ?? null);
    }, [urlConversationId]);

    const handleNewConversation = useCallback(() => {
        navigate('/chat');
        setActiveConversationId(null);
        setChatKey((prev) => prev + 1);
        setSidebarOpen(false);
    }, [navigate]);

    const handleSidebarNavigate = useCallback(() => {
        setSidebarOpen(false);
    }, []);

    useEffect(() => {
        getAccessTokenSilently({
            authorizationParams: {
                audience: config.apiAuth0Audience as string,
                scope: mcpAllowedScopes.join(' '),
            },
        })
            .then(setChatToken)
            .catch((err) => {
                notification.error({
                    message: unwrapError(err),
                    duration: 3,
                });
            });
    }, [getAccessTokenSilently, config.apiAuth0Audience]);

    const sidebar = (
        <ChatSidebar
            activeConversationId={activeConversationId}
            onNewConversation={handleNewConversation}
            onNavigate={isMobile ? handleSidebarNavigate : undefined}
        />
    );

    return (
        <div
            style={{
                display: 'flex',
                height: isMobile ? '100vh' : '100%',
                width: '100%',
                flex: 1,
            }}
        >
            {isMobile ? (
                <Drawer
                    placement="left"
                    open={sidebarOpen}
                    onClose={() => setSidebarOpen(false)}
                    width={280}
                    styles={{ body: { padding: 0 } }}
                >
                    {sidebar}
                </Drawer>
            ) : (
                sidebar
            )}
            <div style={{ flex: 1, minWidth: 0 }}>
                <Chat
                    key={urlConversationId ?? `new-${chatKey}`}
                    chatToken={chatToken}
                    onConversationChange={setActiveConversationId}
                    navBreadcrumb={
                        isMobile ? (
                            <a
                                onClick={() => navigate('/')}
                                style={{
                                    fontSize: 12,
                                    color: '#888',
                                    cursor: 'pointer',
                                }}
                            >
                                &larr; Back to SaaSquatch
                            </a>
                        ) : undefined
                    }
                    menuButton={
                        isMobile ? (
                            <Button
                                icon={<MenuOutlined />}
                                onClick={() => setSidebarOpen(true)}
                            />
                        ) : undefined
                    }
                />
            </div>
        </div>
    );
}
